/**
 * ChainTree — fractal tree view of The Line.
 *
 * Each Orchist is a node; each branch traces an Acknowledgment back to
 * its inviter, all the way up to The First (no inviter).
 *
 * MVP rendering: nested <ul> with subtle indent + connecting lines drawn
 * via left-border. Per sacred/lineage.md, fancy physics / D3 / react-flow
 * is V1 — when adoption warrants. The tree is the art; the structure is
 * already meaningful at zero physics cost.
 *
 * Free-Will Doctrine: disowned Orchists still appear in the tree (their
 * branch is real, the recognition happened) but render dimmed + with a
 * "released" marker. Their children remain rooted — disowning self
 * doesn't unmake the branches you opened.
 *
 * Phase G.7.8 — FAB search (Fuzzy AND Boolean):
 *   When a search query is active, the tree filters to ONLY nodes in the
 *   "lineage path" of any matching Orchist — meaning matches plus their
 *   ancestor chain back to a root. This preserves the doctrinal context
 *   ("Acknowledged by ...") even while filtering. Matched nodes render
 *   bright; ancestors-only render dimmed to indicate they're context, not
 *   the match.
 *
 * Algorithm:
 *   1. Group rows by invitationSentBy (parent id → children).
 *   2. Roots = rows with invitationSentBy === null.
 *   3. If search active, compute matchIds + ancestorPath set.
 *   4. Render recursively, pruning nodes not in ancestorPath.
 *
 * @scale-marker stage-a — at ~1k Orchists, the recursive render bumps
 *   against browser limits. Migrate to react-flow with viewport culling +
 *   collapsible subtrees. The lineage-path search algorithm transfers
 *   directly. Sovereign Watcher emails at threshold.
 * @scale-marker stage-b — at ~100k, switch to server-side cluster fetch
 *   for collapsed subtree representations + ltree-based ancestor query
 *   for path expansion of matches.
 * @scale-marker stage-c — at ~100M, fractal-zoom canvas with tile-based
 *   fetching by (x, y, zoom). Search results highlight in the cosmic view.
 */

import { useMemo, useState } from 'react';
import { Glyph } from '@/components/Glyph';
import { utcToLocalDateString, resolveTimezone } from '@/lib/dates';
import { useAuth } from '@/contexts/AuthContext';
import {
  useLineage,
  lineageDisplayString,
  isFirstOrchist,
  isDisowned,
  type LineageRow,
} from '@/hooks/useLineage';
import { useNicknames, type NicknameRow } from '@/hooks/useNicknames';
import { useEmailShares, type EmailShareRow } from '@/hooks/useEmailShares';
import {
  OrchistSearchInput,
  matchesOrchistQuery,
} from '@/components/OrchistSearch';
import { AnonymityToggleButton } from '@/components/AnonymityToggleButton';

export function ChainTree() {
  const { loading, rows, error, refresh } = useLineage();
  const { contact } = useAuth();
  const { byTargetId: nicknamesByTargetId } = useNicknames();
  const { byOwnerContactId: emailsByOwnerId } = useEmailShares();
  const viewerTz = resolveTimezone(contact?.timezone);

  const [searchQuery, setSearchQuery] = useState('');

  const byParent = useMemo(() => {
    const map = new Map<string | null, LineageRow[]>();
    for (const row of rows) {
      const parent = row.invitationSentBy ?? null;
      if (!map.has(parent)) map.set(parent, []);
      map.get(parent)!.push(row);
    }
    return map;
  }, [rows]);

  const byId = useMemo(() => {
    const map = new Map<string, LineageRow>();
    for (const r of rows) map.set(r.id, r);
    return map;
  }, [rows]);

  // Compute matching ids + their ancestor lineage path. Null = no filter.
  const { matchIds, inPathIds } = useMemo(() => {
    if (!searchQuery.trim()) {
      return { matchIds: null as Set<string> | null, inPathIds: null as Set<string> | null };
    }
    const matches = new Set<string>();
    for (const row of rows) {
      if (
        matchesOrchistQuery(
          row,
          nicknamesByTargetId.get(row.id) ?? null,
          searchQuery,
          viewerTz,
          emailsByOwnerId.get(row.id) ?? null
        )
      ) {
        matches.add(row.id);
      }
    }
    // Walk each match up its parent chain, adding all visited ids to inPath.
    const inPath = new Set<string>();
    for (const id of matches) {
      let current: string | null = id;
      while (current && !inPath.has(current)) {
        inPath.add(current);
        current = byId.get(current)?.invitationSentBy ?? null;
      }
    }
    return { matchIds: matches, inPathIds: inPath };
  }, [searchQuery, rows, nicknamesByTargetId, viewerTz, byId]);

  const roots = byParent.get(null) ?? [];
  const visibleRoots = inPathIds
    ? roots.filter((r) => inPathIds.has(r.id))
    : roots;

  if (loading) {
    return (
      <div className="text-center py-8 text-ink/40 italic font-display">
        The Chain gathers...
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-8 text-destructive text-sm">
        The Chain could not be read: {error.message}
      </div>
    );
  }

  if (roots.length === 0) {
    return (
      <div className="text-center py-8 text-ink/40 italic font-display">
        The Chain is empty.
      </div>
    );
  }

  const isSearching = searchQuery.trim().length > 0;
  const matchCount = matchIds?.size ?? 0;

  return (
    <div className="space-y-8">
      {/* Inline FAB search */}
      <div className="max-w-md mx-auto">
        <OrchistSearchInput
          value={searchQuery}
          onChange={setSearchQuery}
          placeholder="Search The Chain by name, nickname, or date..."
          summary={
            isSearching
              ? `${matchCount.toLocaleString()} match${matchCount === 1 ? '' : 'es'} · context branches preserved`
              : undefined
          }
        />
      </div>

      {visibleRoots.length === 0 ? (
        <p className="text-center py-8 text-ink/40 italic font-display">
          {isSearching
            ? 'No branches in The Chain match your search.'
            : 'The Chain is empty.'}
        </p>
      ) : (
        <ul className="list-none pl-0 space-y-4">
          {visibleRoots.map((root) => (
            <ChainNode
              key={root.id}
              row={root}
              byParent={byParent}
              depth={0}
              viewerTz={viewerTz}
              inPathIds={inPathIds}
              matchIds={matchIds}
              nickname={nicknamesByTargetId.get(root.id) ?? null}
              nicknamesByTargetId={nicknamesByTargetId}
              emailShare={emailsByOwnerId.get(root.id) ?? null}
              emailsByOwnerId={emailsByOwnerId}
              viewerContactId={contact?.id ?? null}
              onAnonymityToggled={refresh}
            />
          ))}
        </ul>
      )}
    </div>
  );
}

function ChainNode({
  row,
  byParent,
  depth,
  viewerTz,
  inPathIds,
  matchIds,
  nickname,
  nicknamesByTargetId,
  emailShare,
  emailsByOwnerId,
  viewerContactId,
  onAnonymityToggled,
}: {
  row: LineageRow;
  byParent: Map<string | null, LineageRow[]>;
  depth: number;
  viewerTz: string;
  inPathIds: Set<string> | null;
  matchIds: Set<string> | null;
  nickname: NicknameRow | null;
  nicknamesByTargetId: Map<string, NicknameRow>;
  emailShare: EmailShareRow | null;
  emailsByOwnerId: Map<string, EmailShareRow>;
  /** Current viewer's contact id (null when unauthenticated). */
  viewerContactId: string | null;
  /** Refresh callback fired after a successful anonymity toggle. */
  onAnonymityToggled?: () => void;
}) {
  const viewerIsTheInviter = !!viewerContactId && row.invitationSentBy === viewerContactId;
  const allChildren = byParent.get(row.id) ?? [];
  const visibleChildren = inPathIds
    ? allChildren.filter((c) => inPathIds.has(c.id))
    : allChildren;

  const display = lineageDisplayString(row, viewerTz);
  const acknowledgedDate = utcToLocalDateString(
    row.invitationAcceptedAt ?? row.invitationConfirmedAt,
    viewerTz
  );
  const disowned = isDisowned(row);
  const disownedDate = utcToLocalDateString(row.orchistDisownedAt, viewerTz);

  // When searching, distinguish actual matches (full opacity) from
  // ancestor-only nodes that are rendered for context (dimmer).
  const isMatch = matchIds ? matchIds.has(row.id) : true;
  const ancestorOnly = matchIds && !isMatch;

  // Combined opacity: disowned + ancestor-only stack
  let opacity = 'opacity-100';
  if (ancestorOnly && disowned) opacity = 'opacity-30';
  else if (ancestorOnly) opacity = 'opacity-50';
  else if (disowned) opacity = 'opacity-55';

  return (
    <li className={depth > 0 ? 'pl-6 border-l border-ink/15' : ''}>
      <div className={`flex items-start gap-4 py-2 ${opacity}`}>
        {row.glyphSeed ? (
          <div className={`flex-shrink-0 ${disowned ? 'text-ink/55' : 'text-ink/85'}`}>
            <Glyph seed={row.glyphSeed} size={48} label={`${display}'s sigil`} />
          </div>
        ) : (
          <div className="flex-shrink-0 w-12 h-12" aria-hidden />
        )}

        <div className="flex-1 min-w-0 pt-1">
          <p
            className={`display text-base md:text-lg leading-tight ${
              isMatch && matchIds ? 'text-ink' : ''
            }`}
          >
            {display}
          </p>
          {nickname && (
            <p className="text-xs text-accent italic font-display">
              you call them: <span className="text-ink not-italic">{nickname.nickname}</span>
            </p>
          )}
          {emailShare && (
            <p className="text-[0.65rem] text-ink/45 italic font-display">
              <span className="not-italic uppercase tracking-wider text-[0.55rem] text-ink/35">email:</span>{' '}
              <span className="font-mono not-italic text-ink/65">{emailShare.sharedEmail}</span>
            </p>
          )}
          {isFirstOrchist(row) ? (
            <p className="text-xs text-ink/45 italic font-display">
              Self-recognized · The Founding Orchist
            </p>
          ) : row.invitationAnonymous ? (
            <>
              {row.publicIdentityMode !== 'GENERIC' && acknowledgedDate && (
                <p className="text-xs text-ink/45 tracking-wide">
                  Acknowledged {acknowledgedDate}
                </p>
              )}
              <p className="text-xs text-ink/45 italic font-display">
                By an anonymous Orchist
                {viewerIsTheInviter && (
                  <>
                    {' '}
                    <span className="text-ink/25">·</span>{' '}
                    <span className="not-italic text-[0.6rem] tracking-[0.2em] uppercase text-ink/35">
                      you
                    </span>{' '}
                    <AnonymityToggleButton
                      inviteeId={row.id}
                      isAnonymous={true}
                      onSuccess={onAnonymityToggled}
                      compact
                    />
                  </>
                )}
              </p>
            </>
          ) : (
            row.publicIdentityMode !== 'GENERIC' &&
            acknowledgedDate && (
              <p className="text-xs text-ink/45 tracking-wide">
                Acknowledged {acknowledgedDate}
              </p>
            )
          )}
          {disowned && disownedDate && (
            <p className="text-xs text-ink/45 italic font-display pt-1">
              Released · {disownedDate}
            </p>
          )}
        </div>
      </div>

      {visibleChildren.length > 0 && (
        <ul className="list-none pl-0 mt-2 space-y-3">
          {visibleChildren.map((child) => (
            <ChainNode
              key={child.id}
              row={child}
              byParent={byParent}
              depth={depth + 1}
              viewerTz={viewerTz}
              inPathIds={inPathIds}
              matchIds={matchIds}
              nickname={nicknamesByTargetId.get(child.id) ?? null}
              nicknamesByTargetId={nicknamesByTargetId}
              emailShare={emailsByOwnerId.get(child.id) ?? null}
              emailsByOwnerId={emailsByOwnerId}
              viewerContactId={viewerContactId}
              onAnonymityToggled={onAnonymityToggled}
            />
          ))}
        </ul>
      )}
    </li>
  );
}
